import { HttpClient } from '@angular/common/http';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { IAppState } from '@myStore/app.state';
import { Store } from '@ngrx/store';
import { AuthService } from '@services/auth/auth.service';
import { UIThemeService } from '@services/UITheme/UITheme.service';
import { FroalaEditorModule } from 'angular-froala-wysiwyg';
import 'froala-editor/js/plugins.pkgd.min.js';
import { Subject } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { Router } from '@angular/router';
@Component({
  selector: 'app-froala',
  templateUrl: './froala.component.html',
  styleUrls: ['./froala.component.sass'],
})
export class FroalaComponent implements OnInit {
  @ViewChild('froalaEditor') froalaEditorEl: ElementRef | null = null;
  @Input() content = '';
  @Input() public loading = false;
  @Input() public title = '';
  @Input() public isEdit = false;
  @Input() public whoCanEdit: string[] = [];
  @Input() public updateLoading = false;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() public studentWorkReview: any | null = null;
  @Input() public windowHeight?: string | null = null;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() public document?: { [key: string]: any } | null = null;
  @Input() public showNotificationBtn = false;
  @Input() public showPenToEdit = true;
  @Input() public salesManagersEditor = false;
  @Output() showEditBtn = new EventEmitter<void>();
  @Output() private updateMaterialHandler = new EventEmitter<{
    content: string;
    title: string;
  }>();
  @Input() public headerShow = true;
  public titleSubject = new Subject<string>();
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  public editor: any;
  public titleError = '';
  public isMaterial = false;
  public options = {
    charCounterCount: true,
    quickInsertEnabled: false,
    attribution: false,
    tabSpaces: 4,
    imageMultipleStyles: true,
    fontFamily: {
      'Roboto,sans-serif': 'Roboto',
      'Oswald,sans-serif': 'Oswald',
      'Montserrat,sans-serif': 'Montserrat',
      "'Open Sans',sans-serif": 'Open Sans',
      'Arial,Helvetica,sans-serif': 'Arial',
      'Georgia,serif': 'Georgia',
      'Impact,Charcoal,sans-serif': 'Impact',
      'Tahoma,Geneva,sans-serif': 'Tahoma',
      "'Times New Roman',Times,serif": 'Times New Roman',
      'Verdana,Geneva,sans-serif': 'Verdana',
      'Roboto Mono, san-serif': 'Roboto mono',
    },
    imagePaste: true,
    imageCORSProxy: `${environment.baseUrl}api/v1/proxy`,
    imageUploadURL: `${environment.baseUrl}api/v1/s3-files/`,
    imageUploadParams: {
      token: this._authService.getToken('access'),
    },
    shortcutsEnabled: ['bold', 'undo', 'redo', 'italic', 'underline'],
    toolbarButtons: {
      moreRich: {
        buttons: [
          'insertLink',
          'insertImage',
          'insertVideo',
          'insertTable',
          'emoticons',
          'fontAwesome',
          'specialCharacters',
          'embedly',
          'insertHR',
        ],
        align: 'left',
        buttonsVisible: 3,
      },
      moreText: {
        buttons: [
          'bold',
          'italic',
          'underline',
          'strikeThrough',
          'subscript',
          'superscript',
          'fontFamily',
          'fontSize',
          'textColor',
          'backgroundColor',
          'inlineClass',
          'inlineStyle',
          'clearFormatting',
        ],
        align: 'left',
        buttonsVisible: 3,
      },
      moreParagraph: {
        buttons: [
          'alignLeft',
          'alignCenter',
          'formatOLSimple',
          'alignRight',
          'alignJustify',
          'formatOL',
          'formatUL',
          'paragraphFormat',
          'paragraphStyle',
          'lineHeight',
          'outdent',
          'indent',
          'quote',
        ],
        align: 'left',
        buttonsVisible: 3,
      },
    },
    imageEditButtons: [
      'imageReplace',
      'imageAlign',
      'imageRemove',
      '|',
      'imageLink',
      'linkOpen',
      'linkEdit',
      'linkRemove',
      '-',
      'imageDisplay',
      'imageStyle',
      'imageAlt',
      'imageSize',
    ],
    theme: 'dark',
    events: {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      initialized: (e: any) => {
        this.editor = e.getEditor();
        this.editor.opts.imageUploadParams.token =
          this._authService.getToken('access');
      },
      'image.beforeRemove': (e: HTMLElement[]) => {
        const imageURL = e[0];
        const body: { [key: string]: string } = {
          token: this._authService.getToken('access'),
        };

        this._http
          .delete(`${environment.baseUrl}api/v1/s3-files/${imageURL.id}/`, {
            body,
          })
          .subscribe((response?) => {
            return response;
          });
      },
    },
  };

  constructor(
    private _store: Store<IAppState>,
    private _http: HttpClient,
    private _editor: FroalaEditorModule,
    private _authService: AuthService,
    private _router: Router,
    public UIThemeService: UIThemeService
  ) {}

  ngOnInit() {
    this.titleSubject
      .pipe(
        debounceTime(600),
        map((text: string) => this.changeTitle(text))
      )
      .subscribe();
    this.isMaterial = this._router.url.split('/').includes('materials');
  }

  @HostListener('document:keydown', ['$event'])
  saveHandler(event: KeyboardEvent) {
    if (
      (event.ctrlKey && event.code === 'KeyS') ||
      (event.metaKey && event.code === 'KeyS')
    ) {
      event.preventDefault();
      this.updateMaterial();
    }

    if (
      (event.ctrlKey && event.code === 'KeyP') ||
      (event.metaKey && event.code === 'KeyP')
    ) {
      event.preventDefault();
      if (this.editor) {
        this.editor.print.run();
      }
    }
  }

  changeTitleHandler(event: KeyboardEvent) {
    const target = event.target as HTMLInputElement;

    this.titleSubject.next(target.value);
  }

  changeTitle(event: string): void {
    this.title = event;

    if (event) {
      this.titleError = '';
      this.updateMaterialHandler.emit({
        content: this.editor.el.innerHTML,
        title: this.title,
      });
    } else {
      this.titleError = 'Заголовок не может быть пустым';
    }
  }

  updateMaterial(): void {
    this.updateMaterialHandler.emit({
      content: this.editor.el.innerHTML,
      title: this.title,
    });
    this.editor.opts.imageUploadParams.token =
      this._authService.getToken('access');
  }

  changeEditModeStatus() {
    this.showEditBtn.emit();
  }
}
